import React from "react";
import Select from "react-select";
import { IoClose, IoCheckmark } from "react-icons/io5";
import {
  materialUnitOptions,
  supplyApplyOptions,
  boqTypeOptions,
  requiresLeadTime,
} from "../../../constants/boq";

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 44,
    height: 44,
    fontSize: 12,
    borderColor: "#d1d5db",
    borderRadius: 12,
    padding: 0,
  }),
  valueContainer: (base) => ({ ...base, padding: "0 12px" }),
  indicatorsContainer: (base) => ({ ...base, height: 44 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({ ...base, fontSize: 12 }),
};

const fieldWrap = { position: "relative", minWidth: 0 };
const inputStyle = {
  height: 44,
  width: "100%",
  borderRadius: 12,
  border: "1px solid #d1d5db",
  padding: "8px 12px 0 12px",
  fontSize: 14,
  boxSizing: "border-box",
};
const labelStyle = {
  position: "absolute",
  left: 12,
  top: -8,
  fontSize: 11,
  color: "#6b7280",
  backgroundColor: "#fff",
  padding: "0 4px",
};

const ScopeInlineAddItemRow = ({
  pendingItem,
  showUnitCostInput = false,
  isSiteVisit = false,
  onUpdate,
  onSave,
  onCancel,
  isSaving,
}) => {
  const handleNumberChange = (value) => {
    const cleaned = value === "" ? "" : value.replace(/[^0-9]/g, "");
    if (cleaned === "" || (!isNaN(cleaned) && parseInt(cleaned) >= 0)) {
      if (pendingItem.boq_type === "Materials") {
        onUpdate({ quantity: cleaned, days: "" });
      } else {
        onUpdate({ days: cleaned, quantity: "" });
      }
    }
  };

  const isMaterials = pendingItem.boq_type === "Materials";
  const showLeadTime = requiresLeadTime(pendingItem.supply_apply);

  // Compute grid columns dynamically. Image cell was removed (handled on
  // the saved BOQItem/Card instead), so the first column is now Item Name.
  // Order: item_name(1), supply(2), [lead_time], qty, [material_unit],
  //        [unit_cost], actions.
  let _nextCol = 3; // first input column after item(1), supply(2)
  const ltCol = showLeadTime ? _nextCol++ : null;
  const qtyCol = _nextCol++;
  const muCol = isMaterials ? _nextCol++ : null;
  const ucCol = showUnitCostInput ? _nextCol++ : null;
  const specEndCol = _nextCol; // exclusive end for spec span
  const row1Columns = [
    "minmax(0,1.3fr)",
    "minmax(0,1fr)",
    showLeadTime ? "minmax(0,1fr)" : null,
    "minmax(0,0.9fr)",
    isMaterials ? "minmax(0,1fr)" : null,
    showUnitCostInput ? "minmax(0,1fr)" : null,
    "64px",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      style={{
        backgroundColor: "#fff",
        border: "2px solid #fdba74",
        borderRadius: 12,
        padding: 16,
        fontSize: 12,
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: row1Columns,
          gridTemplateRows: "auto auto",
          columnGap: 12,
          rowGap: 12,
          alignItems: "center",
        }}
      >
        {/* Row 1: Item Name */}
        <div style={{ ...fieldWrap, gridRow: 1, gridColumn: 1 }}>
          <input
            style={inputStyle}
            value={pendingItem.item_name || ""}
            onChange={(e) => onUpdate({ item_name: e.target.value })}
          />
          <label style={labelStyle}>Item Name</label>
        </div>

        {/* Row 1: Supply/Apply */}
        <div style={{ ...fieldWrap, gridRow: 1, gridColumn: 2 }}>
          <Select
            value={
              supplyApplyOptions.find((o) => o.value === pendingItem.supply_apply) || null
            }
            onChange={(selected) => {
              const nextVal = selected?.value || null;
              onUpdate({
                supply_apply: nextVal,
                ...(requiresLeadTime(nextVal) ? {} : { lead_time: "" }),
              });
            }}
            options={supplyApplyOptions}
            styles={selectStyles}
            menuPosition="fixed"
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          />
          <label style={labelStyle}>Supply/Apply</label>
        </div>

        {/* Row 1: Lead Time (only when Supply / Supply and Apply) */}
        {showLeadTime && (
          <div style={{ ...fieldWrap, gridRow: 1, gridColumn: ltCol }}>
            <input
              type="text"
              inputMode="numeric"
              style={inputStyle}
              placeholder="Days"
              value={pendingItem.lead_time || ""}
              onChange={(e) =>
                onUpdate({
                  lead_time: e.target.value.replace(/[^0-9]/g, ""),
                })
              }
            />
            <label style={labelStyle}>Lead Time (days)</label>
          </div>
        )}

        {/* Row 1: Qty / Days */}
        <div style={{ ...fieldWrap, gridRow: 1, gridColumn: qtyCol }}>
          <input
            style={inputStyle}
            placeholder={isMaterials ? "Qty" : "Days"}
            value={isMaterials ? pendingItem.quantity || "" : pendingItem.days || ""}
            onChange={(e) => handleNumberChange(e.target.value)}
            type="text"
            inputMode="numeric"
          />
          <label style={labelStyle}>{isMaterials ? "Quantity" : "Days"}</label>
        </div>

        {/* Row 1: Material Unit (only if Materials) */}
        {isMaterials && (
          <div style={{ ...fieldWrap, gridRow: 1, gridColumn: muCol }}>
            <Select
              value={
                materialUnitOptions.find((opt) => opt.value === pendingItem.material_unit) ||
                null
              }
              onChange={(selected) =>
                onUpdate({ material_unit: selected?.value || null })
              }
              options={materialUnitOptions}
              styles={selectStyles}
              menuPosition="fixed"
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            />
            <label style={labelStyle}>Material Unit</label>
          </div>
        )}

        {/* Row 1: Unit Cost */}
        {showUnitCostInput && (
          <div
            style={{
              ...fieldWrap,
              gridRow: 1,
              gridColumn: ucCol,
            }}
          >
            <input
              type="text"
              inputMode="numeric"
              value={pendingItem.unit_cost ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || /^\d+$/.test(v)) onUpdate({ unit_cost: v });
              }}
              placeholder="Enter Unit Cost"
              style={inputStyle}
            />
            <label style={labelStyle}>Unit Cost</label>
          </div>
        )}

        {/* Row 2: Material Type */}
        <div style={{ ...fieldWrap, gridRow: 2, gridColumn: 1 }}>
          <Select
            value={{
              label: pendingItem.boq_type || "Materials",
              value: pendingItem.boq_type || "Materials",
            }}
            onChange={(selected) => {
              const newType = selected?.value || "Materials";
              onUpdate(
                newType === "Materials"
                  ? { boq_type: newType, days: "" }
                  : { boq_type: newType, quantity: "", material_unit: null }
              );
            }}
            options={boqTypeOptions}
            styles={selectStyles}
            menuPosition="fixed"
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
          />
          <label style={labelStyle}>Material Type</label>
        </div>

        {/* Row 2: Specification (spans remaining inner columns) */}
        <div
          style={{
            ...fieldWrap,
            gridRow: 2,
            gridColumn: `2 / ${specEndCol}`,
          }}
        >
          <input
            style={inputStyle}
            value={pendingItem.specification || ""}
            onChange={(e) => onUpdate({ specification: e.target.value })}
            placeholder="Enter specification"
          />
          <label style={labelStyle}>Specifications</label>
        </div>

        {/* Actions (spans both rows) */}
        <div
          style={{
            gridRow: "1 / span 2",
            gridColumn: -1,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <button
            onClick={onSave}
            disabled={isSaving}
            style={{
              padding: 8,
              color: "#16a34a",
              background: "transparent",
              border: "none",
              borderRadius: 8,
              cursor: isSaving ? "not-allowed" : "pointer",
              opacity: isSaving ? 0.5 : 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Save"
          >
            <IoCheckmark style={{ width: 20, height: 20 }} />
          </button>
          <button
            onClick={onCancel}
            disabled={isSaving}
            style={{
              padding: 8,
              color: "#ef4444",
              background: "transparent",
              border: "none",
              borderRadius: 8,
              cursor: isSaving ? "not-allowed" : "pointer",
              opacity: isSaving ? 0.5 : 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Cancel"
          >
            <IoClose style={{ width: 20, height: 20 }} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScopeInlineAddItemRow;
